import { EventFilter } from "./event-filter";
import { EventResults } from "./event-results";
import React, { useReducer, memo } from "react";
import {
  isDateBetween,
  isDateEqual,
  formatDate,
  getStartOfDay,
} from "../../utils/date-functions";
import { isBefore } from "date-fns";
import useSWR from "swr";
import { SelectOption } from "../elements/inputs/select";

export interface IFilterState {
  city: SelectOption | undefined;
  eventType: SelectOption[];
  dateRange: [Date | null, Date | null];
}

export interface IEventProps {
  cities: any;
}

const getFormattedMonth = (event: any) => {
  if (event.startDate && event.endDate) {
    const today = getStartOfDay();
    const startDate = getStartOfDay(event.startDate);
    const endDate = getStartOfDay(event.endDate);
    const isBeforeToday = isBefore(startDate, today);
    if (isBeforeToday && isDateBetween(today, startDate, endDate, true)) {
      return formatDate(today, "MMMM yyyy");
    } else if (!isBeforeToday) {
      return formatDate(startDate, "MMMM yyyy");
    }
    return "Ended";
  } else if (event.startDate) {
    return formatDate(event.startDate, "MMMM yyyy");
  }
};

export const Events = memo(({ cities }: IEventProps) => {
  const [state, setState] = useReducer(
    (oldState: IFilterState, newState: Partial<IFilterState>) => ({
      ...oldState,
      ...newState,
    }),
    {
      city: undefined,
      eventType: [],
      dateRange: [null, null],
    }
  );

  const { data, error } = useSWR(
    state.city ? `events/${state.city.value}_events.json` : null,
    {
      dedupingInterval: 600,
    }
  );
  if (error) {
    console.error("error fetching events");
  }

  const setFilters = (filters: Partial<IFilterState>) => {
    if (filters.city) {
      setState({
        ...filters,
        eventType: [],
        dateRange: [null, null],
      });
    } else {
      setState(filters);
    }
  };

  const filteredEvents = data?.events.filter((event: any) => {
    if (state.city && event.city.id !== state.city.value) {
      return false;
    }
    if (state.eventType.length > 0) {
      const eventTypes = state.eventType.map((type) => type.value);
      if (!eventTypes.includes(event.eventType.id)) {
        return false;
      }
    }
    if (state.dateRange[0] && state.dateRange[1]) {
      if (event.startDate && event.endDate) {
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);
        return (
          isDateBetween(startDate, state.dateRange[0], state.dateRange[1]) ||
          isDateBetween(endDate, state.dateRange[0], state.dateRange[1])
        );
      } else if (event.startDate) {
        const startDate = new Date(event.startDate);
        return isDateBetween(startDate, state.dateRange[0], state.dateRange[1]);
      } else {
        return false;
      }
    } else if (state.dateRange[0]) {
      if (event.startDate && event.endDate) {
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);
        return isDateBetween(state.dateRange[0], startDate, endDate);
      } else if (event.startDate) {
        const startDate = new Date(event.startDate);
        return isDateEqual(startDate, state.dateRange[0]);
      } else {
        return false;
      }
    }
    if (state.eventType.length === 0 && !state.city && !state.dateRange[0]) {
      return false;
    }
    return true;
  });

  const modifiedEvents = filteredEvents?.map((event: any) => ({
    ...event,
    month: getFormattedMonth(event),
  }));
  return (
    <>
      <EventFilter
        eventTypes={
          state.city &&
          cities.filter((city: any) => city.id === state.city?.value)[0]
            .eventTypes
        }
        cities={cities}
        filters={state}
        setFilters={setFilters}
      />
      <div style={{ marginTop: "1rem" }}>
        <EventResults
          loading={!!state.city && !data}
          events={modifiedEvents || []}
        />
      </div>
      {/* <Sheet /> */}
    </>
  );
});
